/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppContext } from '../../../context';
import {
    AvailableInterviewOptions,
    InterviewerAlreadyBookedError,
    MultiDayOption,
    SingleDayOption,
} from '../../../types';
import { Button } from '@goodtimeio/rain';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InterviewerToEventBlockSlotStatus } from '../types';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { getTimezone } from '../../../utils/date-utils';
import { isMobile } from 'react-device-detect';
import { timeZoneList } from '../../../utils/timezones';
import { updateInterviewerAvailability } from '../../../api';
import { useHistory, useParams } from 'react-router-dom';
import InterviewOptionsPicker from '../../../components/InterviewOptions/InterviewOptionPicker';
import ListTimezones from '../../../components/ListTimezones';
import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import Wrapper from '../../../components/Wrapper';

const voidFunction = () => {
    return;
};

const SuperdaySignupPickdaysPage: React.FC = () => {
    const { hash, organizationSlug, customUrl } = useParams<{
        hash: string;
        organizationSlug: string;
        customUrl: string;
    }>();
    const {
        meetIntro,
        selectedDays,
        selectedTimeSlots = [],
        addTimeSlot,
        addTimeSlots,
        addDays,
        addSuperdayTimezone,
        availableInterviewOptions,
        translate,
        linkAvailabilityData,
        setLinkAvailabilityData,
        setInterviewerAvailabilityData,
    } = useContext(AppContext);
    const [isScheduling, setIsScheduling] = useState<boolean>(false);
    const [isLoadingResults] = useState<boolean>(false);
    const [selectedTimezone, setSelectedTimezone] = useState<string>(getTimezone(meetIntro?.data?.interview));
    const [startX, setStartX] = useState<number>(0);
    const [scrollLeft, setScrollLeft] = useState<number>(0);
    const isDownRef = useRef<boolean>(false);
    const setIsDownState = (isDown: boolean) => {
        isDownRef.current = isDown;
    };
    const logo = meetIntro?.data?.organization?.logoUrl;
    const primaryColor = meetIntro?.data?.meetCompanyColor ? meetIntro?.data?.meetCompanyColor : '#6400E0';
    let textColor = '#FFFFFF';
    if (meetIntro?.data?.meetFontColor) {
        textColor = meetIntro?.data?.meetFontColor;
    }
    const history = useHistory();

    const ref = useRef<HTMLDivElement>(null);
    const [availableOptions, setAvailableOptions] = useState<AvailableInterviewOptions>({
        singleDay: new Map<string, SingleDayOption[]>(),
        multiDay: new Map<number, MultiDayOption[]>(),
        timezone: '',
    });

    const [activeAvailableOptions, setActiveAvailableOptions] = useState<AvailableInterviewOptions>({
        singleDay: new Map<string, SingleDayOption[]>(),
        multiDay: new Map<number, MultiDayOption[]>(),
        timezone: '',
    });

    useEffect(() => {
        if (linkAvailabilityData?.data?.completed) {
            history.push(`/i/${organizationSlug}/confirm/${customUrl}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkAvailabilityData]);

    const [isMinAvailabilityCovered, setIsMinAvailabilityCovered] = useState(true);
    useEffect(() => {
        if (linkAvailabilityData?.data?.interviewerLink) {
            const interviewerLink = linkAvailabilityData?.data?.interviewerLink;
            const minAvailability = interviewerLink.settings.minAvailabilityMinutes;
            const eventDuration = linkAvailabilityData?.data?.eventDuration || 0;

            const totalDurationAvailability = (selectedTimeSlots?.length || 0) * eventDuration;
            if (totalDurationAvailability < minAvailability) {
                setIsMinAvailabilityCovered(false);
            } else {
                setIsMinAvailabilityCovered(true);
            }
        }
    }, [selectedTimeSlots.length, linkAvailabilityData]);

    const handleMapSlotsSelected = () => {
        const newSlots: any[] = [];
        const removedGroups = [];
        const singleDaySlots = Object.fromEntries(availableInterviewOptions.singleDay);
        const interviewGroupIndex = linkAvailabilityData?.data?.interviewer?.interviewGroupIndex;
        let interviewTimeBlockIndex = 0;
        for (const slots of Object.values(singleDaySlots)) {
            for (const slot of slots) {
                const hash = `${DateTime.fromISO(slot.duration.startTime, { zone: 'UTC' }).toString()}-${
                    slot.iteration
                }-${slot.interviewBlockId}`;
                const interviewBlockStartTime = slot.interviewBlockStartTime;
                if (selectedTimeSlots.includes(hash)) {
                    newSlots.push({ ...slot, interviewBlockStartTime });
                } else {
                    removedGroups.push({
                        interviewGroupIndex,
                        iteration: slot.iteration || 0,
                        blockStartTime: interviewBlockStartTime || '',
                        interviewTimeBlockIndex: slot.interviewTimeBlockIndex ?? interviewTimeBlockIndex,
                    });
                }
            }
            interviewTimeBlockIndex++;
        }
        return { newSlots, removedGroups };
    };
    useEffect(() => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone) {
            if (
                !meetIntro?.data?.interview.settings.isOnsite &&
                meetIntro?.data?.guest.settings.timeZone !== timezone
            ) {
                addSuperdayTimezone(timezone);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (availableInterviewOptions.singleDay.size) {
            const autoLoadDays = getAutoLoadDays(availableInterviewOptions);
            selectedDays?.forEach((day) => autoLoadDays.add(day));
            const autoLoadList = Array.from(autoLoadDays);
            addDays(autoLoadList);
        }

        if (availableInterviewOptions) {
            setAvailableOptions(availableInterviewOptions);
        }

        if (!availableInterviewOptions.singleDay.size) {
            history.push(`/i/${organizationSlug}/intro/${customUrl}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        availableInterviewOptions,
        activeAvailableOptions.timezone,
        meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes,
    ]);

    useEffect(() => {
        if (selectedDays?.length) {
            setActiveAvailableOptions(getActiveInterviewOptions(selectedDays));
        } else {
            setActiveAvailableOptions({
                singleDay: new Map<string, SingleDayOption[]>(),
                multiDay: new Map<number, MultiDayOption[]>(),
                timezone: '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDays?.length, availableOptions]);

    useEffect(() => {
        if (ref.current) {
            const slider = ref.current;
            slider.addEventListener('mousedown', (e) => {
                setIsDownState(true);
                if (slider?.offsetLeft !== undefined) {
                    setStartX(e.pageX - slider.offsetLeft);
                    setScrollLeft(slider.scrollLeft);
                    slider.classList.add('active');
                }
            });
            slider.addEventListener('mouseleave', () => {
                setIsDownState(false);
                slider.classList.remove('active');
            });
            slider.addEventListener('mouseup', () => {
                setIsDownState(false);
                slider.classList.remove('active');
            });

            slider.addEventListener('mousemove', (event) => {
                if (!isDownRef.current) {
                    return;
                }
                if (isDownRef.current) {
                    event.preventDefault();
                    const x = event.pageX - slider.offsetLeft;
                    const walk = (x - startX) * 3;
                    slider.scrollLeft = scrollLeft - walk;
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, isDownRef.current]);

    const backgroundImage = meetIntro?.data?.meetBackgroundUrlList[1] || meetIntro?.data?.meetBackgroundUrlList[0];
    const dynamicButtonStyle = !selectedTimeSlots.length
        ? {}
        : { background: primaryColor, border: primaryColor, color: textColor };

    const getSingleDayPickers = () => {
        const singleDayPickers: ReactElement[] = [];
        activeAvailableOptions.singleDay.forEach((singleDayOptions, date) => {
            singleDayPickers.push(
                <InterviewOptionsPicker
                    singleDayInterviewOptions={singleDayOptions}
                    companyColor={primaryColor}
                    textColor={textColor}
                    selectedTimeIds={selectedTimeSlots}
                    setSelectedTimeIds={handleSelectTime}
                    setSelectedTimeCategory={handleSelectTimeCategory}
                    date={date}
                    timezone={selectedTimezone}
                    displaySuggestTimesSlots={voidFunction}
                    allowGuestToSuggestTimes={meetIntro?.data?.interview?.settings?.allowGuestToSuggestTimes}
                    allowToSuggestTimesFeatureFlag={false}
                    isAlgoLoading={false}
                />,
            );
        });
        return singleDayPickers;
    };

    const onSelectTimezone = (timezone: string) => {
        setSelectedTimezone(timezone);
        addSuperdayTimezone(timezone);
    };

    const handleSelectTime = (newSelectedTimeId: string) => {
        addTimeSlot(newSelectedTimeId);
    };

    const handleSelectTimeCategory = (day: string, timeCategory: string) => {
        const optionsByDay = availableOptions.singleDay.get(day);

        const result =
            optionsByDay
                ?.filter((currTime) => currTime.timeCategory.label === timeCategory)
                .map((currTime) => currTime.hash) || [];

        addTimeSlots(result);
    };

    const getActiveInterviewOptions = (newSelectedDays: string[]) => {
        const newActiveAvailableOptions: AvailableInterviewOptions = {
            singleDay: new Map<string, SingleDayOption[]>(),
            multiDay: new Map<number, MultiDayOption[]>(),
            timezone: selectedTimezone,
        };

        availableOptions.singleDay.forEach((singleDayOption, date: string) => {
            if (newSelectedDays.includes(date)) {
                newActiveAvailableOptions.singleDay.set(date, singleDayOption);
            } else if (selectedTimeSlots?.length) {
                const selectedTimeSlot = singleDayOption.find((singleDay) =>
                    selectedTimeSlots.includes(singleDay.hash),
                );
                if (selectedTimeSlot) {
                    addTimeSlot(selectedTimeSlot.hash);
                }
            }
        });

        const unsortedArray = [...newActiveAvailableOptions.singleDay];
        unsortedArray.sort(([key1], [key2]) => {
            const startTimeA = DateTime.fromJSDate(new Date(key1));
            const startTimeB = DateTime.fromJSDate(new Date(key2));
            if (startTimeA > startTimeB) {
                return 1;
            }
            return -1;
        });
        newActiveAvailableOptions.singleDay = new Map<string, SingleDayOption[]>(unsortedArray);
        return newActiveAvailableOptions;
    };

    const onSchedule = async () => {
        const { newSlots, removedGroups } = handleMapSlotsSelected();
        setIsScheduling(true);
        try {
            const etchUserId = linkAvailabilityData?.data?.interviewer.etchUserId ?? '';
            const eventDesiredOrder = linkAvailabilityData?.data?.interviewer.eventDesiredOrder;
            const superdayId = linkAvailabilityData?.data?.interviewer.superdayId;
            const eventId = linkAvailabilityData?.data?.interviewer.eventId;
            const interviewGroupIndex = linkAvailabilityData?.data?.interviewer.interviewGroupIndex;
            const data = {
                removedGroups,
                interviewerLinkId: linkAvailabilityData?.data?.interviewerLink.id || '',
                etchUserId,
                interviewerEventBlockSlots: newSlots.map((slot) => ({
                    etchUserId,
                    interviewGroupIndex,
                    iteration: slot.iteration,
                    blockStartTime: slot.interviewBlockStartTime,
                    interviewTimeBlockIndex: slot.interviewTimeBlockIndex || 0,
                    eventId,
                    eventDesiredOrder: eventDesiredOrder,
                    superdayId,
                    startTime: slot.duration.startTime,
                    endTime: slot.duration.endTime,
                    status: InterviewerToEventBlockSlotStatus.FREE,
                })),
            };
            const timeRanges = data.interviewerEventBlockSlots.map((slot) => ({
                startTime: slot.startTime,
                endTime: slot.endTime,
                durationMin: linkAvailabilityData?.data?.eventDuration || 0,
            }));
            setInterviewerAvailabilityData({ timeRanges });
            await updateInterviewerAvailability(data);
            setIsScheduling(false);
            if (linkAvailabilityData?.data) {
                setLinkAvailabilityData({ ...linkAvailabilityData.data, completed: true });
            }
        } catch (e) {
            if (e instanceof InterviewerAlreadyBookedError) {
                setIsScheduling(false);
                history.push(`/time-already-booked/${hash}`);
            }
        }
    };

    const interviewerLink = linkAvailabilityData?.data?.interviewerLink;
    const minAvailability = interviewerLink?.settings.minAvailabilityMinutes || 0;
    const minAvailabilityLabel = minAvailability < 60 ? 'minutes' : 'hours';
    const minAvailabilityValue = minAvailability < 60 ? minAvailability : minAvailability / 60;
    return (
        <Wrapper
            loading={meetIntro?.isLoading || isLoadingResults}
            backgroundImage={backgroundImage}
            logo={logo}
            message={translate('intro:calculatingAndBuildingInterviewSchedules')}
        >
            <div className="scheduleContainer">
                <div className="titleOnsiteTimezone">
                    {meetIntro?.data?.interview.settings.isOnsite ? (
                        <>
                            <b> {translate('pickDays:onSiteInterview')} </b>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                <div className="listTimezonesContainer">
                    <ListTimezones
                        selectedValue={{
                            value: selectedTimezone,
                            label: '',
                        }}
                        onChange={(val) => onSelectTimezone(val)}
                        options={timeZoneList}
                        isDisabled={meetIntro?.data?.interview?.settings.isOnsite ?? false}
                    />
                </div>
                {selectedDays?.length ? (
                    <>
                        <div className="optionPickerContainer" ref={ref}>
                            <>
                                {getSingleDayPickers().map((element, index) => (
                                    <React.Fragment key={`single-day-interview-option-picker-${index}`}>
                                        {element}
                                    </React.Fragment>
                                ))}
                            </>
                        </div>
                        {selectedTimeSlots.length > 0 && !isMobile ? (
                            <div className="optionPickerContainerFooter">
                                <b>{translate('pickDays:lookingGood')}&nbsp;</b>
                                {translate('pickDays:forGreaterFlexibility')}
                            </div>
                        ) : null}
                        {!isMinAvailabilityCovered && (
                            <div className="optionPickerContainerFooter">
                                <b style={{ color: '#c33556' }}>
                                    {translate('signup:pickDays:minAvailability', {
                                        value: minAvailabilityValue,
                                        label: minAvailabilityLabel,
                                    })}
                                </b>
                            </div>
                        )}

                        <div className="footerContainer">
                            <Button
                                disabled={!selectedTimeSlots.length || !isMinAvailabilityCovered}
                                onClick={onSchedule}
                                className={'scheduleBtn'}
                                style={dynamicButtonStyle}
                                hidden={isMobile && !selectedTimeSlots.length}
                            >
                                {isScheduling ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    translate('signup:pickDays:submitAvailability')
                                )}
                            </Button>
                        </div>
                    </>
                ) : null}
            </div>
        </Wrapper>
    );
};

const getAutoLoadDays = (availableInterviewOptions: AvailableInterviewOptions) => {
    const daysForAutoLoad = new Set<string>(Array.from(availableInterviewOptions?.singleDay)?.map(([day]) => day));
    return daysForAutoLoad;
};

export default SuperdaySignupPickdaysPage;
